/**
 * contextOf — controllers 번들 위에 GameContext(항상-최신 getter 모음)를 1회 생성한다.
 *
 * get()이 호출 시점의 번들을 반환하므로, applySaveData가 번들을 통째 스왑해도
 * 기존 ctx 보유자는 stale 참조 없이 최신 컨트롤러를 읽는다 (getter 주입 규약의 번들 일반화).
 * GameScene.gameContext()의 수동 getter 21줄을 이 헬퍼 1줄로 대체 — 신규 컨트롤러 추가 시
 * GameScene 무접촉 (DOMAIN_CONTROLLER_KEYS 누락은 아래 타입 게이트가 컴파일 에러로 차단).
 */
import type { DomainControllers } from "./createControllers.js";
import type { GameContext } from "./GameContext.js";

/** DomainControllers 키 목록 — 런타임 순회용 SSoT. */
export const DOMAIN_CONTROLLER_KEYS = [
  "chapter",
  "economy",
  "specBuilder",
  "moduleRegistry",
  "inventory",
  "playerModuleLevels",
  "moduleMastery",
  "moduleSynergy",
  "synergyLevels",
  "spaceship",
  "grid",
  "upgradeInv",
  "idSource",
  "goldBox",
  "diamondBox",
  "rebirth",
  "statUpgrade",
  "rebirthUpgrade",
  "relic",
  "dungeon",
  "dailyShop",
  "fusion",
  "mysteryBlock",
  "guideQuest",
  "saveSystem",
  "settingsStore",
  "analytics",
  "analyticsMilestones",
  "soundController",
] as const satisfies readonly (keyof DomainControllers)[];

/** 누락 게이트 — DomainControllers에 새 키가 생기면 이 상수가 그 키 리터럴 타입이 되어 `true` 대입이 컴파일 에러. */
type MissingControllerKey = Exclude<
  keyof DomainControllers,
  (typeof DOMAIN_CONTROLLER_KEYS)[number]
>;
export const domainControllerKeysExhaustive: [MissingControllerKey] extends [never]
  ? true
  : MissingControllerKey = true;

export function contextOf(get: () => DomainControllers): GameContext {
  const ctx = {} as Record<string, () => unknown>;
  for (const key of DOMAIN_CONTROLLER_KEYS) {
    ctx[key] = () => get()[key];
  }
  return ctx as GameContext;
}
